// A small ring buffer of recent page errors, attached to Feedback reports.
//
// INSTALLED FOR EVERY VISITOR, FIRST THING (`src/instrumentation-client.ts`),
// so load and hydration errors are already in it when a team member opens the
// launcher. For everyone else it is two listeners and a console.error wrapper
// feeding at most ten short strings in memory: nothing is read, sent or
// stored server-side unless a team member files a report. Credentials and
// email addresses are masked as each line is recorded (`./redact`). Kept free
// of zod and anything heavy because it ships in the boot bundle.
import { redactFeedbackText } from "./redact";

const feedbackReportMaxConsoleErrors = 10;
const feedbackReportMaxConsoleMessageLength = 500;
type CapturedConsoleError = {
  readonly kind: "error" | "unhandledrejection" | "console";
  readonly message: string;
  readonly at: string;
};
const buffer: CapturedConsoleError[] = [];
let installed = false;
/** Work budget for serialising one console argument, in characters. */
const stringifyBudget = feedbackReportMaxConsoleMessageLength * 2;

/**
 * Never throws, and stops early on huge values: this runs inside every
 * console.error on every page, on whatever the page logged. Page values can
 * throw anything when read (getters, toJSON, proxies) and can be circular or
 * hold BigInts, so each attempt falls back to the next — JSON, then String(),
 * then a placeholder — rather than letting a log line break the page.
 */
function stringify(value: unknown): string {
  if (typeof value === "string") {
    return value.slice(0, stringifyBudget);
  }

  if (value instanceof Error) {
    const stackLine = value.stack?.split("\n")[1]?.trim();
    return `${value.name}: ${value.message}${stackLine ? ` (${stackLine})` : ""}`.slice(
      0,
      stringifyBudget,
    );
  }

  let budget = stringifyBudget;

  try {
    const json = JSON.stringify(value, (_key, child: unknown) => {
      if (budget <= 0) return undefined;

      budget -= typeof child === "string" ? child.length : 8;
      // A replacer cannot stop JSON.stringify from visiting every array index.
      // Give it a bounded copy before it starts traversing the array.
      return Array.isArray(child)
        ? child.slice(0, Math.max(0, Math.floor(budget / 8)))
        : child;
    });
    return (json ?? String(value)).slice(0, stringifyBudget);
  } catch {
    // Arbitrary page values: see above. Not an error in our code.
  }

  try {
    return String(value).slice(0, stringifyBudget);
  } catch {
    return "[unserializable]";
  }
}

function record({
  kind,
  parts,
}: {
  readonly kind: CapturedConsoleError["kind"];
  readonly parts: unknown[];
}) {
  const message = redactFeedbackText(parts.map(stringify).join(" ").trim());

  if (!message) {
    return;
  }

  buffer.push({
    kind,
    message:
      message.length > feedbackReportMaxConsoleMessageLength
        ? `${message.slice(0, feedbackReportMaxConsoleMessageLength - 1)}…`
        : message,
    at: new Date().toISOString(),
  });

  if (buffer.length > feedbackReportMaxConsoleErrors) {
    buffer.splice(0, buffer.length - feedbackReportMaxConsoleErrors);
  }
}

export function installFeedbackConsoleCapture(): void {
  if (installed || typeof window === "undefined") {
    return;
  }

  installed = true;
  window.addEventListener("error", (event) => {
    record({ kind: "error", parts: [event.error ?? event.message] });
  });
  window.addEventListener("unhandledrejection", (event) => {
    record({ kind: "unhandledrejection", parts: [event.reason] });
  });
  const originalError = console.error.bind(console);
  // The page's own console.error always runs, even if recording fails.
  console.error = (...args: unknown[]) => {
    try {
      record({ kind: "console", parts: args });
    } finally {
      originalError(...args);
    }
  };
}

export function readFeedbackConsoleErrors(): CapturedConsoleError[] {
  return buffer.slice();
}
